<script setup lang="ts">
import { onBeforeUnmount, useTemplateRef } from 'vue';
import { animations } from '@/services/Animations';

interface Props {
  actionTaken: boolean;
  consentLabel: string;
  consentText: string;
  declineLabel: string;
  privacyPolicyLabel: string;
  title: string;
}

interface PulseAnimation {
  stopImmediately?: () => void;
}

defineProps<Props>();

// biome-ignore lint/correctness/noUnusedVariables: used in <template>
const emit = defineEmits<{
  consent: [];
  decline: [];
  openPrivacyPolicy: [];
}>();

const consentButton = useTemplateRef<HTMLButtonElement>('consentButton');
let pulseAnimation: PulseAnimation | null = null;

function pulseConsentButton(): void {
  if (!consentButton.value) {
    return;
  }

  pulseAnimation =
    animations.pulseBox(consentButton.value, { count: 3 }) ?? null;
}

defineExpose({ pulseConsentButton });

onBeforeUnmount(() => {
  pulseAnimation?.stopImmediately?.();
  pulseAnimation = null;
});
</script>

<template>
  <div class="GDPRConsent__content" data-testid="gdpr-root">
    <h1
      class="GDPRConsent__title kiosk-title"
      data-testid="gdpr-title"
    >
      {{ title }}
    </h1>

    <p class="GDPRConsent__text" data-testid="gdpr-consent-text">
      {{ consentText }}
    </p>

    <div class="GDPRConsent__buttons">
      <button
        class="GDPRConsent__button GDPRConsent__button--decline kiosk-button-inline"
        data-testid="gdpr-decline"
        :disabled="actionTaken"
        type="button"
        @click="emit('decline')"
      >
        {{ declineLabel }}
      </button>

      <button
        ref="consentButton"
        class="GDPRConsent__button GDPRConsent__button--consent kiosk-button"
        data-testid="gdpr-consent"
        :disabled="actionTaken"
        type="button"
        @click="emit('consent')"
      >
        {{ consentLabel }}
      </button>
    </div>

    <button
      class="GDPRConsent__privacy-link"
      data-testid="gdpr-privacy-link"
      type="button"
      @click="emit('openPrivacyPolicy')"
    >
      {{ privacyPolicyLabel }}
    </button>
  </div>
</template>

<style scoped lang="scss">
.GDPRConsent__content {
  text-align: center;
  max-width: 75vw;
  margin: 0 auto;
  padding: 3rem 2rem;
}

.GDPRConsent__title {
  margin-bottom: 2rem;
}

.GDPRConsent__text {
  font-size: 1.4rem;
  line-height: 1.8;
  margin-bottom: 3rem;
  color: var(--Kiosk--font_color);
}

.GDPRConsent__buttons {
  display: flex;
  gap: 1.5rem;
  justify-content: center;
  align-items: center;
  margin-bottom: 2rem;
  flex-wrap: wrap;
}

.GDPRConsent__button {
  min-width: 200px;
}

.GDPRConsent__button:disabled {
  opacity: 0.5;
  pointer-events: none;
}

.GDPRConsent__privacy-link {
  display: inline-block;
  background: none;
  border: none;
  padding: 0;
  color: var(--Kiosk--font_color);
  text-decoration: underline;
  font-size: 1.1rem;
  font-family: inherit;
  cursor: pointer;
  opacity: 0.8;
}

.GDPRConsent__privacy-link:hover {
  opacity: 1;
}
</style>
